import { inject, Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BiometricAuthError, BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric';

import { AuthFailureCause } from '../model/failure';
import { BiometryKind } from '../model/tokens';
import { AUTH_TELEMETRY } from '../telemetry/auth-telemetry';
import { errorDetail } from '../telemetry/error-detail';

import {
  BIOMETRIC_MAX_ATTEMPTS,
  BIOMETRIC_PROMPT_COPY,
  BiometricError,
  BiometricSignIn,
  CREDENTIAL_SERVER,
  ENROLLMENT_KEY,
  EnrollmentState,
} from './biometric-sign-in';

/**
 * The availability error codes that mean the OS has locked biometrics.
 *
 * `USER_LOCKOUT` is the permanent kind — recoverable by a device-credential unlock, despite the name
 * — and `USER_TEMPORARY_LOCKOUT` lapses on its own after 30 seconds. Neither destroys key material,
 * which is why the cause they produce is non-terminal.
 */
const LOCKOUT_AVAILABILITY_CODES: ReadonlySet<BiometricAuthError> = new Set([
  BiometricAuthError.USER_LOCKOUT,
  BiometricAuthError.USER_TEMPORARY_LOCKOUT,
]);

/**
 * What each sensor the plugin can report is called on a button.
 *
 * A `Record` rather than a `switch` with a default, so a member added to the plugin's enum is a
 * compile error here instead of silently landing on a fallback. The wrong answer is not a crash — it
 * is a button naming a sensor the device does not have.
 *
 * Every Android biometric maps to one member on purpose: Apple's guidance is to name the technology
 * exactly, Android's is to stay generic, because the framework chooses which sensor answers.
 * `NONE` and `DEVICE_CREDENTIAL` both map to `deviceCredential`, and both exist to be refused —
 * `canEnroll` asks with `useFallback: false`, so reaching this map at all means something biometric
 * was reported.
 */
const BIOMETRY_KINDS: Readonly<Record<BiometryType, BiometryKind>> = {
  [BiometryType.NONE]: 'deviceCredential',
  [BiometryType.DEVICE_CREDENTIAL]: 'deviceCredential',
  [BiometryType.TOUCH_ID]: 'touchId',
  [BiometryType.FACE_ID]: 'faceId',
  [BiometryType.FINGERPRINT]: 'biometric',
  [BiometryType.FACE_AUTHENTICATION]: 'biometric',
  [BiometryType.IRIS_AUTHENTICATION]: 'biometric',
  [BiometryType.MULTIPLE]: 'biometric',
};

/**
 * Biometric sign-in on iOS and Android, via `verifyIdentity` and the plugin's credential store.
 *
 * `verifyIdentity` is the only plugin method that forwards `maxAttempts` to `AuthActivity`
 * (`NativeBiometric.java:245-257`), which is why the prompt is raised through it rather than through a
 * gated read: the secure-data methods cannot be passed a retry count at all, so `AuthActivity:84`
 * defaults it to 1 and cancels the OS prompt on the first non-match.
 *
 * It is also the only method that forwards `allowedBiometryTypes`, which is the other half of
 * Cap-go#105 — so nothing here needs `authValidityDuration`, and nothing here can crash on it.
 */
@Injectable()
export class NativeBiometricSignIn implements BiometricSignIn {
  /**
   * Where the two failures nothing else can see are reported.
   *
   * This port is the only thing that learns them: a failed delete is not allowed to fail its caller, and
   * a write that cannot be read back is not visible to anyone until the next sign-in fails.
   */
  private readonly telemetry = inject(AUTH_TELEMETRY);

  async enrollment(): Promise<EnrollmentState> {
    try {
      const { value } = await Preferences.get({ key: ENROLLMENT_KEY });
      if (value === 'true') return 'enrolled';
      // `main`'s vocabulary, kept verbatim so an upgraded device reads its own preference correctly.
      if (value === 'do not ask') return 'declined';
      return 'notEnrolled';
    } catch {
      // A launch that cannot read a preference must still finish, and `notEnrolled` is the answer that
      // lets it: the sign-in form renders and the button is simply not offered.
      return 'notEnrolled';
    }
  }

  async canEnroll(): Promise<boolean> {
    try {
      const { isAvailable } = await NativeBiometric.isAvailable({ useFallback: false });
      return isAvailable;
    } catch {
      return false;
    }
  }

  async biometry(): Promise<BiometryKind> {
    try {
      const { isAvailable, biometryType } = await NativeBiometric.isAvailable({ useFallback: false });
      if (!isAvailable) return 'none';
      // Defended rather than trusted: this crosses the bridge, so a value outside the enum is a
      // possibility the type system cannot rule out. Something biometric can authenticate — it said
      // so — and the generic word is the one that is true of whatever it turns out to be.
      return BIOMETRY_KINDS[biometryType] ?? 'biometric';
    } catch {
      return 'none';
    }
  }

  /**
   * Prompt, then read.
   *
   * The two options that matter are each single-platform, and together they cover both: `maxAttempts`
   * is Android-only, while `useFallback` and `fallbackTitle` are iOS-only. On Android
   * `BiometricPrompt` treats the `DEVICE_CREDENTIAL` authenticator and a negative button as mutually
   * exclusive, so the plugin ignores `useFallback` there and the way past a dead sensor is the negative
   * button — which returns to the form underneath.
   *
   * A read that fails after a satisfied prompt is reported the same way a refusal is, and the machine
   * treats both alike: neither forgets the credentials, because neither is evidence they are wrong.
   */
  async signIn(): Promise<{ email: string; password: string }> {
    try {
      await NativeBiometric.verifyIdentity({
        ...BIOMETRIC_PROMPT_COPY,
        useFallback: true,
        maxAttempts: BIOMETRIC_MAX_ATTEMPTS,
      });
    } catch (error) {
      throw new BiometricError(await this.refineLockout(this.classify(error)));
    }

    try {
      const { username, password } = await NativeBiometric.getCredentials({ server: CREDENTIAL_SERVER });
      return { email: username, password };
    } catch {
      // The prompt was satisfied and the item still could not be read. `vaultUnavailable` rather than a
      // refusal, because nobody refused anything — and it is deliberately not terminal here: the
      // machine's `biometricSignInFailed` arm keeps both the credentials and the Durable Root.
      throw new BiometricError({ kind: 'vaultUnavailable' });
    }
  }

  async saveCredentials(email: string, password: string): Promise<void> {
    await NativeBiometric.setCredentials({ server: CREDENTIAL_SERVER, username: email, password });
  }

  /**
   * Whether an item is there to replay.
   *
   * `false` when the query fails, which is the answer that withholds the sign-in screen's button: a
   * button over credentials that may not exist prompts and then finds nothing.
   */
  async hasCredentials(): Promise<boolean> {
    try {
      const { isSaved } = await NativeBiometric.isCredentialsSaved({ server: CREDENTIAL_SERVER });
      return isSaved;
    } catch {
      return false;
    }
  }

  async enroll(): Promise<void> {
    await Preferences.set({ key: ENROLLMENT_KEY, value: 'true' });
  }

  async disable(): Promise<void> {
    await this.deleteCredentials();
    await Preferences.set({ key: ENROLLMENT_KEY, value: 'false' });
  }

  async decline(): Promise<void> {
    await this.deleteCredentials();
    await Preferences.set({ key: ENROLLMENT_KEY, value: 'do not ask' });
  }

  /**
   * Delete the keychain item, without letting a failure fail the caller — and without hiding one.
   *
   * Both callers are making the same statement, "these credentials must not be replayed again", and
   * neither can act on a failure: {@link disable} and {@link decline} have already recorded the choice by
   * the time this runs. So this resolves either way.
   *
   * **It is reported rather than swallowed, and that is a correction.** This used to catch and say
   * nothing, on the reasoning that an item already gone satisfies the request — which is true, and is not
   * the only thing that reaches here. Both platforms tolerate absence on their own (iOS treats
   * `errSecItemNotFound` as success in both delete paths, Android's `deleteEntry` is a no-op), so an error
   * arriving here is a *genuine* failure, and it is the one that leaves a password on a device that asked
   * for it gone. On Android it can also leave the ciphertext behind while removing its key — the plugin
   * deletes the Keystore entry first and the `SharedPreferences` entries second — and an item in that
   * state can never be read again, while `isCredentialsSaved` goes on answering `true` for it.
   */
  private async deleteCredentials(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER });
    } catch (error) {
      this.telemetry.credentialStoreFailed('delete', errorDetail(error));
    }
  }

  /**
   * Recover a lockout the prompt could not report.
   *
   * On iOS `LAContext` has no biometry-specific lockout status to hand back through this path, so a
   * lockout and a refusal arrive identically. The availability query is the way out through the
   * published API: its error code is drawn from an enum that does carry both lockout kinds.
   *
   * Only `biometricRejected` is refined. A cancel is unambiguous, a lockout already says so, and
   * `unexpectedFailure` must not be promoted into a refusal — guessing is how someone gets a
   * misreported failure over a bug.
   *
   * The race worth knowing about: a temporary lockout can lapse between the prompt and the query, in
   * which case it presents as a plain rejection. That is the milder misreport and both are
   * non-terminal, so it costs a retry rather than a session. Not worth a mechanism.
   */
  private async refineLockout(cause: AuthFailureCause): Promise<AuthFailureCause> {
    if (cause.kind !== 'biometricRejected') return cause;
    return (await this.lockedOut()) ? { kind: 'biometricLockedOut' } : cause;
  }

  /**
   * Whether the OS has locked biometrics, asked of biometrics alone.
   *
   * Asked with `useFallback: false`: with it on, the plugin answers `isAvailable` from the device
   * passcode whenever biometrics cannot be evaluated and attaches no error code, so on any device
   * with a passcode a lockout would read as working biometrics.
   *
   * Narrowed to the two lockout codes rather than taken as a whole, because nothing enrolled and no
   * hardware are different answers, and reporting either as a lockout would tell someone to wait for
   * something that is never coming. Answered rather than thrown: this refines a failure that is
   * already being reported and must not replace it with its own.
   */
  private async lockedOut(): Promise<boolean> {
    try {
      const { isAvailable, errorCode } = await NativeBiometric.isAvailable({ useFallback: false });
      return !isAvailable && errorCode !== undefined && LOCKOUT_AVAILABILITY_CODES.has(errorCode);
    } catch {
      return false;
    }
  }

  /**
   * Map a prompt failure onto a cause the machine can route on.
   *
   * The plugin's codes, recorded here because we depend on them and they are documented nowhere we
   * own. Every code `verifyIdentity` can reject with, and which platform produces it:
   *
   * | Code | Plugin name | iOS | Android |
   * | --- | --- | --- | --- |
   * | `2` | `USER_LOCKOUT` | `biometryLockout` | `ERROR_LOCKOUT_PERMANENT` |
   * | `4` | `USER_TEMPORARY_LOCKOUT` | never | `ERROR_LOCKOUT`, and `AuthActivity`'s own "too many attempts" |
   * | `10` | `AUTHENTICATION_FAILED` | `authenticationFailed` | the prompt exhausting `maxAttempts` |
   * | `11` | `APP_CANCEL` | app cancelled it | — |
   * | `15` | `SYSTEM_CANCEL` | `systemCancel` | `ERROR_TIMEOUT`, `ERROR_CANCELED` |
   * | `16` | `USER_CANCEL` | `userCancel` | `ERROR_USER_CANCELED`, `ERROR_NEGATIVE_BUTTON` |
   * | `17` | `USER_FALLBACK` | the passcode button | — |
   *
   * An error with no recognised code is `unexpectedFailure`, not a refusal. Every `biometric*` cause
   * implies a person answered a prompt, so reporting a bug as a refusal would put a failure nobody
   * caused on the path a refusal takes.
   */
  private classify(error: unknown): AuthFailureCause {
    const code = String((error as { code?: unknown })?.code ?? '');

    if (/USER_CANCEL|USER_FALLBACK|APP_CANCEL|SYSTEM_CANCEL|^11$|^15$|^16$|^17$/.test(code)) {
      return { kind: 'biometricCancelled' };
    }
    if (/USER_LOCKOUT|USER_TEMPORARY_LOCKOUT|^2$|^4$/.test(code)) {
      return { kind: 'biometricLockedOut' };
    }
    if (/AUTHENTICATION_FAILED|^10$/.test(code)) {
      return { kind: 'biometricRejected' };
    }
    return { kind: 'unexpectedFailure' };
  }
}
